import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    IconButtonProps,
    MenuItem,
    Popover,
    Typography,
    useTheme,
} from '@mui/material';

import React, { useState } from 'react'
import { IsEmpty, IsNotEmpty } from '../Helpers';
import BusySpinner from './BusySpinner';
import AddActionButton from './AddActionButton';

export interface IconButtonExProps extends Omit<IconButtonProps, 'onClick'> {
    onClick?: (event?: React.MouseEvent<HTMLButtonElement>) => (void | Promise<void>);
    headerContent?: string | React.ReactNode;
    hoverContent?: string | React.ReactNode;
    message?: string | React.ReactNode;
    visibleLabel?: string;
    menuItemLabel?: React.ReactNode;
    onMenuAction?: () => void;
}

const IconButtonEx = ({ headerContent = 'Are you sure?', hoverContent, message, onClick, visibleLabel, menuItemLabel, onMenuAction, ...props }: IconButtonExProps) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);

    const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
        onMenuAction?.();
        if (IsEmpty(message)) // if the message is empty, there is no point in displaying the confirmation.  just run it.
            await onClick(event as React.MouseEvent<HTMLButtonElement>);
        else
            setOpen(true);
    };

    const handleConfirm = async (event: React.MouseEvent<HTMLButtonElement>) => {
        try {
            setBusy(true);
            await onClick(event);
            setOpen(false);
        } catch (error) {

        } finally {
            setBusy(false);
        }
    };

    const handlePopoverOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };
    const popoverOpen = IsNotEmpty(hoverContent) && Boolean(anchorEl);

    const trigger = menuItemLabel
        ? <MenuItem disabled={props.disabled} aria-label={props['aria-label']} onClick={handleClick}>{menuItemLabel}</MenuItem>
        : IsNotEmpty(visibleLabel)
        ? <AddActionButton
            label={String(visibleLabel)}
            disabled={props.disabled}
            color={props.color === 'default' ? 'primary' : props.color}
            className={props.className}
            sx={props.sx}
            aria-label={props['aria-label'] ?? String(visibleLabel)}
            onClick={handleClick}
        />
        : <IconButton
            size="small"
            {...props}
            onClick={handleClick}
            aria-owns={popoverOpen ? 'mouse-over-popover' : undefined}
            aria-haspopup={IsNotEmpty(hoverContent)}
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
        />;

    return <>
        {trigger}
        <Dialog aria-labelledby="customized-dialog-title" open={open} fullWidth={true} color="red">
            <BusySpinner busy={busy}>
                <DialogTitle id="customized-dialog-title">
                    {headerContent}
                </DialogTitle>
                <DialogContent dividers sx={{ padding: theme.spacing(2) }}>
                    <DialogContentText id="alert-dialog-slide-description">
                        {message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ margin: 0, padding: theme.spacing(1) }}>
                    <Button variant='contained' onClick={handleConfirm}>Ok</Button>
                    <Button onClick={() => setOpen(false)} color="inherit">Cancel</Button>
                </DialogActions>
            </BusySpinner>
        </Dialog>
        <Popover
            id="mouse-over-popover"
            sx={{ pointerEvents: 'none', padding: theme.spacing(1) }}
            open={popoverOpen}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus>
            <Typography sx={{ padding: theme.spacing(.5), backgroundColor: theme.palette.background.neutral }}>{hoverContent}</Typography>
        </Popover>
    </>;
}

export default IconButtonEx;
